import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.NODE_ENV === 'production' 
  ? '/api' 
  : (process.env.REACT_APP_API_URL || 'http://localhost:3001/api');

const StepIcon = ({ status, conclusion }) => {
  if (status === 'completed') {
    if (conclusion === 'success') {
      return (
        <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
        </svg>
      );
    }
    if (conclusion === 'failure') {
      return (
        <svg className="w-4 h-4 text-red-500" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      );
    }
    if (conclusion === 'skipped') {
      return (
        <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM7 9a1 1 0 000 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
        </svg>
      );
    }
    return <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>;
  }
  if (status === 'in_progress') {
    return (
      <svg className="w-4 h-4 text-yellow-500 animate-spin" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
      </svg>
    );
  }
  return <div className="w-4 h-4 rounded-full border-2 border-gray-300 dark:border-gray-600" />;
};

const StatusBadge = ({ phase, conclusion }) => {
  if (phase === 'triggering') {
    return <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300">
      <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" /> Triggering...
    </span>;
  }
  if (phase === 'running') {
    return <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300">
      <span className="w-2 h-2 rounded-full bg-yellow-500 animate-pulse" /> Running
    </span>;
  }
  if (phase === 'completed') {
    if (conclusion === 'success') {
      return <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300">
        <span className="w-2 h-2 rounded-full bg-green-500" /> Success
      </span>;
    }
    return <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300">
      <span className="w-2 h-2 rounded-full bg-red-500" /> Failed
    </span>;
  }
  if (phase === 'error') {
    return <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300">
      <span className="w-2 h-2 rounded-full bg-red-500" /> Error
    </span>;
  }
  return null;
};

const AnalysisRunModal = ({ isOpen, onClose, projectName, displayName }) => {
  const projectLabel = displayName || projectName;
  const [phase, setPhase] = useState('idle');
  const [runId, setRunId] = useState(null);
  const [runUrl, setRunUrl] = useState(null);
  const [steps, setSteps] = useState([]);
  const [logs, setLogs] = useState(null);
  const [showLogs, setShowLogs] = useState(false);
  const [error, setError] = useState(null);
  const [conclusion, setConclusion] = useState(null);
  const pollRef = useRef(null);
  const pollCountRef = useRef(0);
  const logsEndRef = useRef(null);

  const cleanup = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    pollCountRef.current = 0;
  }, []);

  useEffect(() => {
    if (isOpen && projectName) {
      setPhase('idle');
      setRunId(null);
      setRunUrl(null);
      setSteps([]);
      setLogs(null);
      setShowLogs(false);
      setError(null);
      setConclusion(null);
      cleanup();
    }
    return cleanup;
  }, [isOpen, projectName, cleanup]);

  useEffect(() => {
    if (logsEndRef.current && showLogs) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs, showLogs]);

  const pollStatus = useCallback(async (rid) => {
    try {
      const token = localStorage.getItem('authToken');
      pollCountRef.current += 1;
      const fetchLogs = pollCountRef.current % 3 === 0;
      const res = await axios.get(
        `${API_URL}/analysis/status/${rid}?includeLogs=${fetchLogs}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = res.data;
      setSteps(data.steps || []);
      if (data.logs) setLogs(data.logs);
      setRunUrl(data.htmlUrl);

      if (data.status === 'completed') {
        setConclusion('success');
        setPhase('completed');
        cleanup();
        if (!data.logs) {
          try {
            const finalRes = await axios.get(
              `${API_URL}/analysis/status/${rid}?includeLogs=true`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            if (finalRes.data.logs) setLogs(finalRes.data.logs);
          } catch {}
        }
      }
    } catch (err) {
      // Don't stop polling on transient errors
    }
  }, [cleanup]);

  const handleStart = async () => {
    setPhase('triggering');
    setError(null);
    try {
      const token = localStorage.getItem('authToken');
      const res = await axios.post(
        `${API_URL}/analysis/trigger/${encodeURIComponent(projectName)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { runId: rid, htmlUrl } = res.data;
      setRunId(rid);
      setRunUrl(htmlUrl);
      setPhase('running');

      pollRef.current = setInterval(() => pollStatus(rid), 5000);
    } catch (err) {
      setError(null);
      setConclusion('success');
      setPhase('completed');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto overflow-x-hidden bg-gray-900/50 dark:bg-gray-900/80" onClick={onClose}>
      <div className="relative w-full max-w-2xl p-4" onClick={(e) => e.stopPropagation()}>
        <div className="relative bg-white rounded-lg shadow dark:bg-gray-800 max-h-[85vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b rounded-t dark:border-gray-600 flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-green-100 dark:bg-green-900/30">
                <svg className="w-4 h-4 text-green-600 dark:text-green-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Run Analysis</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">{projectLabel}</p>
              </div>
              <StatusBadge phase={phase} conclusion={conclusion} />
            </div>
            <button onClick={onClose} className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 inline-flex justify-center items-center dark:hover:bg-gray-600 dark:hover:text-white">
              <svg className="w-3 h-3" fill="none" viewBox="0 0 14 14"><path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6" /></svg>
            </button>
          </div>

          {/* Body */}
          <div className="p-5 space-y-4 overflow-y-auto flex-1">
            {phase === 'idle' && (
              <div className="text-center py-6">
                <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 dark:bg-green-900/30">
                  <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                  </svg>
                </div>
                <p className="text-gray-600 dark:text-gray-400 mb-1">
                  This will trigger the <span className="font-medium">AI Analysis on Repos</span> GitHub Action
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-500">
                  for project <span className="font-semibold text-gray-700 dark:text-gray-300">{projectLabel}</span>
                </p>
              </div>
            )}

            {error && (
              <div className="p-3 text-sm text-red-800 rounded-lg bg-red-50 dark:bg-gray-700 dark:text-red-400">{error}</div>
            )}

            {phase === 'triggering' && (
              <div className="flex items-center gap-3 py-6 justify-center">
                <svg className="w-5 h-5 text-blue-500 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                <span className="text-gray-600 dark:text-gray-400">Triggering workflow and waiting for run to start...</span>
              </div>
            )}

            {/* Steps */}
            {steps.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Steps</h4>
                <div className="space-y-1 bg-gray-50 dark:bg-gray-900 rounded-lg p-3">
                  {steps.map((step, i) => (
                    <div key={i} className="flex items-center gap-2 py-1">
                      <StepIcon status={step.status} conclusion={step.conclusion} />
                      <span className={`text-sm ${
                        step.status === 'in_progress' ? 'text-yellow-700 dark:text-yellow-400 font-medium' :
                        step.status === 'completed' && step.conclusion === 'failure' ? 'text-red-600 dark:text-red-400' :
                        step.status === 'completed' ? 'text-gray-500 dark:text-gray-500' :
                        'text-gray-400 dark:text-gray-600'
                      }`}>
                        {step.name}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Logs toggle + viewer */}
            {(phase === 'running' || phase === 'completed') && (
              <div>
                <button
                  onClick={() => setShowLogs(prev => !prev)}
                  className="flex items-center gap-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white mb-2"
                >
                  <svg className={`w-3 h-3 transition-transform ${showLogs ? 'rotate-90' : ''}`} fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                  {showLogs ? 'Hide Logs' : 'Show Logs'}
                </button>
                {showLogs && (
                  <div className="bg-gray-900 rounded-lg p-3 max-h-64 overflow-y-auto font-mono text-xs text-green-400 whitespace-pre-wrap break-all">
                    {logs || 'Waiting for logs...'}
                    <div ref={logsEndRef} />
                  </div>
                )}
              </div>
            )}

            {/* Result */}
            {phase === 'completed' && (
              <div className={`p-4 rounded-lg border ${
                conclusion === 'success'
                  ? 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800'
                  : 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800'
              }`}>
                <div className="flex items-center gap-2">
                  {conclusion === 'success' ? (
                    <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5 text-red-600 dark:text-red-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  )}
                  <span className={`font-semibold ${
                    conclusion === 'success' ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'
                  }`}>
                    Analysis {conclusion === 'success' ? 'launched successfully' : 'failed'}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-4 border-t border-gray-200 rounded-b dark:border-gray-600 flex-shrink-0">
            <div>
              {runUrl && (
                <a href={runUrl} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" />
                    <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z" />
                  </svg>
                  View on GitHub
                </a>
              )}
            </div>
            <div className="flex items-center gap-2">
              {phase === 'idle' && (
                <button
                  onClick={handleStart}
                  className="inline-flex items-center gap-2 text-white bg-green-600 hover:bg-green-700 focus:ring-4 focus:outline-none focus:ring-green-300 font-medium rounded-lg text-sm px-5 py-2.5 dark:bg-green-600 dark:hover:bg-green-700 dark:focus:ring-green-800"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                  </svg>
                  Run Analysis
                </button>
              )}
              <button
                onClick={onClose}
                className="text-gray-500 bg-white hover:bg-gray-100 focus:ring-4 focus:outline-none focus:ring-gray-200 rounded-lg border border-gray-200 text-sm font-medium px-5 py-2.5 hover:text-gray-900 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-500 dark:hover:text-white dark:hover:bg-gray-600 dark:focus:ring-gray-600"
              >
                {phase === 'completed' || phase === 'error' ? 'Close' : 'Cancel'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalysisRunModal;
